import {useMemo, useState} from 'react';
import MonthBreakdown from './MonthBreakdown.jsx';
import {kr, pct, monthShort} from '../lib/format.js';

/**
 * Every column sortable. Money columns are summed in the footer; MARGINS ARE
 * RECOMPUTED from the sums rather than averaged — the mean of eight monthly margins
 * is not the period's margin unless every month had identical revenue.
 *
 * The footer totals only fully-booked months. Adding a partly-booked August into a
 * sum would understate cost and overstate the result, which is the misreading this
 * whole view keeps trying to prevent.
 */
const COLUMNS = [
	{key: 'month', label: 'Period', get: m => m.label ?? monthShort(m.month), align: 'left'},
	{key: 'revenue', label: 'Omsättning', fmt: kr, total: true},
	{key: 'gross_profit', label: 'Bruttovinst', fmt: kr, total: true},
	{key: 'gross_margin', label: 'Bruttomarginal', fmt: pct, ratio: ['gross_profit', 'revenue']},
	{key: 'ebitda', label: 'EBITDA', fmt: kr, total: true, strong: true},
	{key: 'ebitda_margin', label: 'EBITDA-marginal', fmt: pct, ratio: ['ebitda', 'revenue']},
	{key: 'direct', label: 'Direkta kostnader', fmt: kr, total: true},
	{key: 'external', label: 'Övriga externa', fmt: kr, total: true},
	{key: 'personnel', label: 'Personalkostnader', fmt: kr, total: true},
	{key: 'financial', label: 'Finansiella poster', fmt: kr, total: true},
	{key: 'operating_cost', label: 'Driftkostnad', fmt: kr, total: true},
	{key: 'operating_per_day', label: 'Drift/dygn', fmt: kr},
	{key: 'result', label: 'Resultat', fmt: kr, strong: true, total: true},
	{key: 'mrr', label: 'MRR', fmt: kr},
	{key: 'customers', label: 'Kunder', fmt: v => v ?? '—'},
	{key: 'nrr', label: 'NRR', fmt: pct},
	{key: 'cash', label: 'Likvida medel', fmt: kr},
];


export default function MonthTable({months}) {
	// Newest period first: the row you came to read is the most recent one, and with 66
	// months of history an ascending sort puts it below the fold every time.
	const [sort, setSort] = useState({key: 'month', dir: 'desc'});
	const rows = useMemo(() => {
		const dir = sort.dir === 'asc' ? 1 : -1;
		return [...months].sort((a, b) => {
			const x = a[sort.key], y = b[sort.key];
			if (x == null) return 1;
			if (y == null) return -1;
			return (typeof x === 'string' ? x.localeCompare(y) : x - y) * dir;
		});
	}, [months, sort]);

	const totals = useMemo(() => {
		const complete = months.filter(m => !m.ledger_partial);
		const sum = k => complete.reduce((a, b) => a + (b[k] ?? 0), 0);
		const t = {};
		for (const c of COLUMNS) {
			if (c.total) t[c.key] = sum(c.key);
			else if (c.ratio) t[c.key] = sum(c.ratio[1]) ? sum(c.ratio[0]) / sum(c.ratio[1]) : null;
		}
		// Cost per day over the whole period, not the mean of the rows' rates: February's
		// daily rate is genuinely higher than January's for the same spend, and averaging
		// the rates would weight a 28-day month like a 31-day one. Each row carries its
		// own day count (a quarter's is the three months'), so this holds at every grain.
		const days = complete.reduce((a, m) => a + m.days, 0);
		t.operating_per_day = days ? sum('operating_cost') / days : null;
		return t;
	}, [months]);

	const toggle = key => setSort(s =>
		s.key === key ? {key, dir: s.dir === 'asc' ? 'desc' : 'asc'} : {key, dir: 'desc'});

	// One period open at a time. Opening a second closes the first — two Resultatrapporter
	// side by side is more scrolling than comparison.
	const [open, setOpen] = useState(null);

	if (!months.length) return null;

	return (
		<div className="table-wrap">
			<table>
				<thead>
					<tr>{COLUMNS.map(c => (
						<th key={c.key} className="sortable" onClick={() => toggle(c.key)}
							aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
							{c.label}
							{sort.key === c.key && <i className={`sort-arrow sort-arrow--${sort.dir}`} />}
						</th>
					))}</tr>
				</thead>
				<tbody>
					{rows.map(m => [
						<tr key={m.month} className={m.ledger_partial ? 'row--current' : undefined}
							onClick={() => setOpen(o => (o === m.month ? null : m.month))}
							style={{cursor: 'pointer'}}
							aria-expanded={open === m.month}>
							{COLUMNS.map(c => (
								<td key={c.key} className={c.strong ? 'num--strong' : undefined}>
									{c.key === 'month' && (open === m.month ? '− ' : '+ ')}
									{c.get ? c.get(m) : c.fmt(m[c.key])}
									{c.key === 'month' && m.ledger_partial && <span className="pill">delvis</span>}
									{c.key === 'month' && m.months_n < m.months_expected && !m.ledger_partial
										&& <span className="pill">{m.months_n} mån</span>}
								</td>
							))}
						</tr>,
						...(open === m.month
							? [<MonthBreakdown key={`${m.month}-md`} month={m.month} label={m.label}
								columnCount={COLUMNS.length} />]
							: []),
					]).flat()}
				</tbody>
				<tfoot>
					<tr>
						<td>Summa bokfört</td>
						{COLUMNS.slice(1).map(c => (
							<td key={c.key} className={c.strong ? 'num--strong' : undefined}>
								{totals[c.key] == null ? '—' : c.fmt(totals[c.key])}
							</td>
						))}
					</tr>
				</tfoot>
			</table>
		</div>
	);
}
